/* eslint-disable import/prefer-default-export */
import { CompanySharedSpaceUserProperties } from '../types';
import { TeamSpaceUserRoleType } from '../constants/companySharedConstants';
import api, { apiV2 } from '../lib/baseAxios';
import { AcceptTeamspaceInvitation } from '../features/team/teamSlice';
import { IVFSResponse } from './fileSystemService';
import { TeamSpace } from '../types/state';

// Enable shared space
export const enableSharedSpace = async ({
  companySharedSpaceUsers,
  teamId,
  spaceIds,
}: {
  companySharedSpaceUsers: CompanySharedSpaceUserProperties[];
  teamId: string;
  spaceIds?: [string];
}) => {
  return api.post(`/company-shared-space/enable-company-shared-space`, {
    companySharedSpaceUsers,
    teamId,
    spaceIds,
  });
};

// Deprecated. This endpoint NOT IN USE. New Endpoint -> getTeamSpaceDetails
// Get shared space users
export const getCompanySharedSpaceUsersURL = async () => {
  return api.get(`/company-shared-space/shared-space-users`);
};

// Send emails to shared space users
// @deprecated. This endpoint NOT IN USE
export const sendEmailURL = (emails: string[], teamId: string) => {
  return api.post(`/company-shared-space/send-email-to-team-space-users`, {
    emails,
    teamId,
  });
};

// send invitation and add to teamspace
export const addToTeamspaceURL = (emails: string[], teamId: string) => {
  return api.post(`/company-shared-space/addToTeamspace`, {
    emails,
    teamId,
  });
};

export const sendEmailToNotRegisteredURL = (
  data: [],
  ownerId: string,
  teamId: string
) => {
  return api.post(
    `/company-shared-space/send-email-to-not-registered-team-space-users`,
    {
      data,
      ownerId,
      teamId,
    }
  );
};

// Get admin's plugin videos
// export const getPluginFiles = async () => {
//   return api.get(`/plugin/plugin-video`);
// };

// toggle company shared space user status
export const toggleCompanySharedSpaceUserStatus = (
  userId: string,
  teamId: string
) => {
  return api.post(`/company-shared-space/toggle-shared-space-user-status`, {
    userId,
    teamId,
  });
};

// Delete company shared space user
export const deleteCompanySharedSpaceUser = (data: {
  userId: string;
  teamId: string;
}) => {
  return api.post(`/company-shared-space/delete-shared-space-user`, {
    userId: data.userId,
    teamId: data.teamId,
  });
};

// Change role of company shared space user
export const changeCompanySharedSpaceUserRole = (data: {
  userId: string;
  role: string;
  teamId: string;
}) => {
  return api.post(`/company-shared-space/change-shared-space-user-role`, {
    userId: data.userId,
    role: data.role,
    teamId: data.teamId,
  });
};

export const acceptCompanySharedSpaceInvitationLink = (
  fromUserId: string,
  toUserId: string,
  teamId: string
) => {
  return api.post(`/company-shared-space/accept-company-shared-space-user`, {
    senderId: fromUserId,
    receiverId: toUserId,
    teamId,
  });
};

export const acceptTeamspaceInvitationLink = (
  data: AcceptTeamspaceInvitation
) => {
  return api.post(`/company-shared-space/acceptTeamspaceInvitation`, data);
};

export const getTeamSpaces = async () => {
  return api.get(`/company-shared-space/team-space`);
};

export const getTeamSpaceDetails = async (teamId: string) => {
  return api.get(`/company-shared-space/team-space/${teamId}`);
};

export const getTeamSpaceDeletionStatusURL = async () => {
  return api.get(`/company-shared-space/team-space-deletion-status`);
};

export const createTeamSpace = async (teamName: string) => {
  return api.post(`/company-shared-space/team-space`, { name: teamName });
};

export const getLastSelectedTeamSpace = async () => {
  return api.get(`/company-shared-space/selected-team-space`);
};

export const saveLastSelectedTeamSpace = async (teamId: string) => {
  return api.post(`/company-shared-space/selected-team-space`, { teamId });
};

// Add members to space
interface AddToSpaceRequestBody {
  teamId: string;
  spaceId: string;
  email: string;
  role: TeamSpaceUserRoleType;
}
// Remove once inviteToTeamOrFolder is stable
export const addToSpace = (data: AddToSpaceRequestBody) => {
  return api.post(`/company-shared-space/add-to-space`, data);
};

interface AddToSpaceBulkRequestBody {
  teamId: string;
  folderIds: string[];
  emails: string[];
  role: TeamSpaceUserRoleType;
}
export const inviteToTeamOrFolder = (data: AddToSpaceBulkRequestBody) => {
  return api.post(`/company-shared-space/invite-to-team-or-folder`, data);
};

// Remove members from space
export const removeFromSpace = (data: {
  teamId: string;
  spaceId: string;
  userId: string;
}) => {
  return api.post(`/company-shared-space/remove-from-space`, data);
};

// toggle space user status
export const changeSpaceUserStatus = (data: {
  email: string;
  teamId: string;
  spaceId: string;
}) => {
  return api.post(`/company-shared-space/enable-space-member`, data);
};

export const renameTeam = (data: { teamId: string; newName: string }) => {
  return api.post(`/company-shared-space/rename-team`, data);
};

// V2 endpoints
interface GetInviteIdResponseData {
  joinCode: string;
}
export const getJoinCode = async (teamId: string) => {
  const response = await apiV2.put<IVFSResponse<GetInviteIdResponseData>>(
    `/team/${teamId}/member/join/code`
  );
  const { joinCode } = response.data.data || {};
  return joinCode;
};

export const removeTeamMember = async ({
  teamId,
  userId,
}: {
  teamId: string;
  userId: string;
}) => {
  return apiV2.delete(`/team/${teamId}/member/${userId}`);
};

interface TeamSettings {
  fileOptions: TeamSpace['fileOptions'];
}
export const updateTeamSettings = async ({
  teamId,
  settings,
}: {
  teamId: string;
  settings: TeamSettings;
}) => {
  return apiV2.put(`/team/${teamId}/settings`, settings);
};

// Schedule team deletion
export const scheduleTeamDeletion = async (teamId: string) => {
  return apiV2.put(`/team/${teamId}/deletion-schedule`);
};

// Unschedule team deletion
export const unscheduleTeamDeletion = async (teamId: string) => {
  return apiV2.delete(`/team/${teamId}/deletion-schedule`);
};

export const addWebhookUrl = async ({
  teamId,
  name,
  url,
}: {
  teamId: string;
  name: string;
  url: string;
}) => {
  return apiV2.post(`/team/${teamId}/integrations/webhook`, { name, url });
};

export const removeWebhookUrl = async ({
  teamId,
  url,
}: {
  teamId: string;
  url: string;
}) => {
  return apiV2.delete(`/team/${teamId}/integrations/webhook`, {
    params: { url },
  });
};
